import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowPathIcon,
  ChevronLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  InformationCircleIcon,
  ListBulletIcon,
  QueueListIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { testCaseService, TestCase } from '../services/testCaseService';
import { generateTestCasePDF } from '../utils/testCasePDFGenerator';
import toast from 'react-hot-toast';

export const TestCaseDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [testCase, setTestCase] = useState<TestCase| null>(null);
    const [loading, setLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    
    useEffect(() => {
        if (id) {
            loadTestCase();
        }
    }, [id]);
    
    const loadTestCase = async () => {
        try {
            setLoading(true);
            const response = await testCaseService.getTestCase(id!);
            setTestCase(response.data);
        } catch (err: any) {
            toast.error('Failed to load test case');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDownloadPDF = async () => {
        if (!testCase) return;
        
        setIsDownloading(true);
        const toastId = toast.loading('Generating PDF...');
        
        try {
            const blob = await generateTestCasePDF(testCase);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
            link.download = `${testCase.tcCode || 'TestCase'}_${timestamp}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            toast.success('PDF downloaded successfully', { id: toastId });
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF', { id: toastId });
        } finally {
            setIsDownloading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-app-bg">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-orange-500" />
            </div>
        );
    }
    
    if (!testCase) return null;
    
    return (
        <div className="min-h-screen bg-app-bg p-6">
            <div className="max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-white rounded-full transition-colors group"
                        >
                            <ChevronLeftIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500" />
                        </button>
                        <h1 className="text-3xl font-bold font-heading text-gray-900 tracking-tight">{testCase.tcCode}</h1>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            className="bg-white border-blue-100 text-blue-600 hover:bg-blue-50 hover:border-blue-200 rounded-xl"
                            onClick={handleDownloadPDF}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <>
                                    <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                    Download PDF
                                </>
                            )}
                        </Button>
                        <Button
                            variant="default"
                            className="rounded-xl shadow-lg shadow-orange-500/20"
                            onClick={() => navigate(`/test-cases/${id}/edit`)}
                        >
                            <PencilSquareIcon className="h-4 w-4 mr-1.5" />
                            Edit
                        </Button>
                        <Button
                            variant="outline"
                            className="bg-white border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200 rounded-xl"
                            onClick={() => toast.success('Delete feature coming soon')}
                        >
                            <TrashIcon className="h-4 w-4 mr-1.5" />
                            Delete
                        </Button>
                        <Button
                            variant="outline"
                            className="bg-white border-gray-100 text-gray-600 hover:bg-gray-50 rounded-xl"
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Test Case Details */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <InformationCircleIcon className="h-5 w-5 text-orange-500" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Test Case Details</h2>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Title</label>
                                <p className="text-xl font-bold text-gray-900">{testCase.title}</p>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Description</label>
                                <p className="text-gray-600 leading-relaxed text-base">{testCase.description}</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 border-t border-gray-50">
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">Type</label>
                                    <span className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-xl text-xs font-bold border border-blue-100 uppercase tracking-wide">
                                        {testCase.type || 'Functional'}
                                    </span>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">Status</label>
                                    <span className="bg-green-50 text-green-700 px-4 py-1.5 rounded-xl text-xs font-bold border border-green-100">
                                        {testCase.status || 'Active'}
                                    </span>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">Priority</label>
                                    <span className={`px-4 py-1.5 rounded-xl text-xs font-bold border uppercase tracking-wide ${
                                        testCase.priority === 'High' ? 'bg-red-50 text-red-600 border-red-100' :
                                        testCase.priority === 'Medium' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                                        'bg-blue-50 text-blue-600 border-blue-100'
                                    }`}>
                                        {testCase.priority || 'Medium'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pre-conditions */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <ListBulletIcon className="h-5 w-5 text-orange-500" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Pre-conditions</h2>
                        </div>
                        <ul className="space-y-4">
                            {testCase.preConditions && testCase.preConditions.length > 0 ? (
                                testCase.preConditions.map((condition, idx) => (
                                    <li key={idx} className="flex items-start gap-4 text-gray-600 bg-gray-50/50 p-4 rounded-xl border border-gray-100/50">
                                        <div className="h-6 w-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">
                                            {idx + 1}
                                        </div>
                                        <span className="text-base font-medium">{condition}</span>
                                    </li>
                                ))
                            ) : <p className="text-gray-400 italic bg-gray-50 p-6 rounded-2xl text-center border border-dashed border-gray-200">No pre-conditions specified.</p>}
                        </ul>
                    </div>

                    {/* Test Steps */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <QueueListIcon className="h-5 w-5 text-orange-500" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Test Steps</h2>
                        </div>
                        <div className="space-y-4">
                            {testCase.steps && testCase.steps.length > 0 ? (
                                testCase.steps.map((step, idx) => (
                                    <div key={idx} className="bg-gray-50/30 rounded-2xl border border-gray-100 p-6 transition-all hover:bg-gray-50/50">
                                        <div className="flex items-center gap-3 mb-4">
                                            <span className="text-xs font-black text-orange-500 uppercase tracking-widest bg-orange-50 px-2 py-1 rounded">Step {idx + 1}</span>
                                            <div className="h-px flex-1 bg-gray-100" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Action:</label>
                                                <p className="text-sm text-gray-800 leading-relaxed font-semibold">{step.action}</p>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Expected Result:</label>
                                                <p className="text-sm text-gray-800 leading-relaxed font-semibold">{step.expectedResult}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : <p className="text-gray-400 italic bg-gray-50 p-6 rounded-2xl text-center border border-dashed border-gray-200">No test steps specified.</p>}
                        </div>
                    </div>

                    {/* Post-conditions */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <CheckCircleIcon className="h-5 w-5 text-orange-500" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Post-conditions</h2>
                        </div>
                        <ul className="space-y-4">
                            {testCase.postConditions && testCase.postConditions.length > 0 ? (
                                testCase.postConditions.map((condition, idx) => (
                                    <li key={idx} className="flex items-start gap-4 text-gray-600 bg-gray-50/50 p-4 rounded-xl border border-gray-100/50">
                                        <div className="h-6 w-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">
                                            {idx + 1}
                                        </div>
                                        <span className="text-base font-medium">{condition}</span>
                                    </li>
                                ))
                            ) : <p className="text-gray-400 italic bg-gray-50 p-6 rounded-2xl text-center border border-dashed border-gray-200">No post-conditions specified.</p>}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
